import { useMemo } from "react";
import { computeAssembly } from "@/utils/assemblyCalc";
import { formatINR } from "@/utils/formatINR";
import { useAuthedImage } from "@/utils/authedImage";

const BASIS_LABELS = {
  components_total: "% of Components Total (A)",
  process_cost:     "% of Assembly Process (B)",
  sub_total:        "% of Sub-Total",
  lump_sum:         "Lump Sum",
  gross_rmc:        "% of Components Total (A)",
  net_rmc:          "% of Components Total (A)",
};

export default function AssemblyQuoteSheet({ asm, componentNpds, customerName, settings, unit }) {
  const calc = useMemo(() => computeAssembly(asm || {}, componentNpds || []), [asm, componentNpds]);
  // R30 polish — fetch images via authenticated api (no token in URL).
  const screenshotUrl = useAuthedImage(
    asm?.assembly_drawing_screenshot_path && asm?.assembly_id
      ? `/assemblies/${asm.assembly_id}/screenshot`
      : null,
    [asm?.assembly_id, asm?.assembly_drawing_screenshot_path]
  );
  const signatureUrl = useAuthedImage(
    asm?.approved_for_export && asm?.approved_by ? `/users/${asm.approved_by}/signature` : null,
    [asm?.approved_for_export, asm?.approved_by]
  );
  if (!asm) return null;

  const companyName = settings?.company_name || "Anmol Metcomp Private Limited";
  // Round 21.2 — Use unit address if non-empty; else fall back to global company_address.
  const companyAddress = (unit && unit.address) ? unit.address : (settings?.company_address || "");
  const tnc = settings?.tnc_template || "";
  const gstLine = settings?.gst_mandatory_line || "";
  const tmPctRaw = Number(asm.assembly_tool_maintenance_pct || 0);
  const tmPct = tmPctRaw > 0 && tmPctRaw < 1.5 ? tmPctRaw * 100 : tmPctRaw;
  const surfNotes = asm.assembly_surface_treatment?.notes || "";
  const heatNotes = asm.assembly_heat_treatment?.notes || "";

  return (
    <div className="quote-sheet text-slate-800 text-[12px] leading-snug relative">
      {!asm.approved_for_export && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10 overflow-hidden" aria-hidden="true" data-testid="draft-watermark">
          <div className="text-[160px] font-black text-red-500 opacity-[0.12] -rotate-45 select-none whitespace-nowrap tracking-widest">
            DRAFT
          </div>
        </div>
      )}
      {/* Header */}
      <div className="grid grid-cols-[80px_1fr] border border-slate-700 mb-2">
        <div className="bg-white flex items-center justify-center p-1 row-span-2">
          <img src="/aampl-logo.png" alt="AAMPL" className="max-w-full max-h-[55px]" />
        </div>
        <div className="bg-orange-500 text-white font-bold text-center py-1.5 text-base">{companyName}</div>
        <div className="text-center italic text-slate-500 text-[11px] py-1 border-t border-slate-700">
          {companyAddress || "—"}
        </div>
      </div>

      <div className="bg-amber-200 text-slate-800 font-bold text-center py-1.5 border border-slate-700 text-sm mb-2">
        ASSEMBLY COST ESTIMATE
      </div>

      {/* Quotation header */}
      <table className="w-full border-collapse border border-slate-700 mb-2">
        <tbody>
          <tr>
            <td className="border border-slate-700 p-1 px-2 w-[28mm] font-semibold">Quotation No.</td>
            <td className="border border-slate-700 p-1 px-2">{asm.assembly_number || ""}</td>
            <td className="border border-slate-700 p-1 px-2 w-[28mm] font-semibold">Date</td>
            <td className="border border-slate-700 p-1 px-2">{new Date().toISOString().slice(0, 10)}</td>
          </tr>
          <tr>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Revision</td>
            <td className="border border-slate-700 p-1 px-2">R{asm.current_round || 0}</td>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Validity</td>
            <td className="border border-slate-700 p-1 px-2">30 Days</td>
          </tr>
          <tr>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Customer</td>
            <td className="border border-slate-700 p-1 px-2">{customerName || "—"}</td>
            <td className="border border-slate-700 p-1 px-2 font-semibold">End Customer</td>
            <td className="border border-slate-700 p-1 px-2">{asm.end_customer || "—"}</td>
          </tr>
        </tbody>
      </table>

      {/* Assembly Details */}
      <SectionBar letter="" title="ASSEMBLY DETAILS" />
      <table className="w-full border-collapse border border-slate-700">
        <tbody>
          <tr>
            <td className="border border-slate-700 p-1 px-2 w-[28mm] font-semibold">Assembly / Part #</td>
            <td className="border border-slate-700 p-1 px-2 w-1/3">{`${asm.assembly_name || ""} / ${asm.part_number || ""}`}</td>
            <td className="border border-slate-700 p-1 px-2 w-[28mm] font-semibold">Model</td>
            <td className="border border-slate-700 p-1 px-2 w-1/3">{asm.model_name || "—"}</td>
          </tr>
          <tr>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Drawing No.</td>
            <td className="border border-slate-700 p-1 px-2">{asm.drawing_number || "—"}</td>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Application</td>
            <td className="border border-slate-700 p-1 px-2">{asm.application || "—"}</td>
          </tr>
          <tr>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Status</td>
            <td className="border border-slate-700 p-1 px-2">{(asm.status || "draft").toUpperCase()}</td>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Min Order Qty (assemblies)</td>
            <td className="border border-slate-700 p-1 px-2" data-testid="assembly-quote-moq">
              {calc.customer_moq_pcs != null
                ? `${calc.customer_moq_is_lower_bound ? "≥ " : ""}${Number(calc.customer_moq_pcs).toLocaleString()} assemblies`
                : "—"}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Section A: Components */}
      <SectionBar letter="A" title="COMPONENTS (SUB-PARTS)" />
      <div className="flex gap-3 items-stretch">
        <div className="flex-1 min-w-0">
          <table className="w-full border-collapse border border-slate-700">
            <thead className="bg-amber-200 font-bold">
              <tr>
                <th className="border border-slate-700 p-1 w-10">#</th>
                <th className="border border-slate-700 p-1 w-32">NPD #</th>
                <th className="border border-slate-700 p-1 text-left">Part Name</th>
                <th className="border border-slate-700 p-1 w-28">Part #</th>
                <th className="border border-slate-700 p-1 w-16 text-right">Qty</th>
                <th className="border border-slate-700 p-1 w-24 text-right">₹/pc</th>
                <th className="border border-slate-700 p-1 w-28 text-right">Line ₹</th>
              </tr>
            </thead>
            <tbody>
              {(calc.component_lines || []).length === 0 && (
                <tr><td colSpan={7} className="border border-slate-700 p-2 text-center italic text-slate-400">No sub-parts</td></tr>
              )}
              {(calc.component_lines || []).map((line, i) => (
                <tr key={i}>
                  <td className="border border-slate-700 p-1 text-center">{i + 1}</td>
                  <td className="border border-slate-700 p-1 px-2 font-mono">{line.npd_number || "—"}</td>
                  <td className={`border border-slate-700 p-1 px-2 ${line.missing ? "italic text-red-600" : ""}`}>
                    {line.missing ? "(component NPD missing)" : (line.part_name || "")}
                  </td>
                  <td className="border border-slate-700 p-1 px-2 font-mono text-xs">{line.part_number || ""}</td>
                  <td className="border border-slate-700 p-1 px-2 text-right font-mono">{Number(line.qty_per_assembly || 0).toFixed(2)}</td>
                  <td className="border border-slate-700 p-1 px-2 text-right font-mono">{formatINR(line.unit_price)}</td>
                  <td className="border border-slate-700 p-1 px-2 text-right font-mono">{formatINR(line.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {asm.assembly_drawing_screenshot_path && (
          <div className="w-[280px] flex-shrink-0 border border-slate-300 bg-white p-2 flex flex-col">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
              Drawing
            </div>
            <div className="flex-1 flex items-center justify-center">
              {screenshotUrl && (
                <img
                  src={screenshotUrl}
                  alt="Drawing preview"
                  className="max-w-full max-h-[320px] object-contain"
                />
              )}
            </div>
            {asm.drawing_number && (
              <div className="text-[10px] text-slate-500 text-center mt-1 truncate">
                Dwg: {asm.drawing_number}
              </div>
            )}
          </div>
        )}
      </div>
      <SectionTotal letter="A" label="Components Total (A)" value={calc.components_total} />

      {/* Section B */}
      <SectionBar letter="B" title="ASSEMBLY PROCESS COST" />
      <ItemTable
        headers={["#", "Process", "₹/pc"]}
        widths={["w-10", "", "w-28 text-right"]}
        rows={(asm.assembly_processes || []).map((p, i) => [i + 1, p.name || "", formatINR(p.cost || 0)])}
      />
      <SectionTotal letter="B" label="Total Assembly Process Cost (B)" value={calc.assembly_process_cost} />

      {/* Section C */}
      <SectionBar letter="C" title="ASSEMBLY BOUGHT OUT PARTS" />
      <ItemTable
        headers={["#", "Item", "Qty", "₹/unit", "Total ₹"]}
        widths={["w-10", "", "w-16 text-right", "w-24 text-right", "w-28 text-right"]}
        rows={(asm.assembly_bop_items || []).map((b, i) => {
          const qty = Number(b.qty || 0); const up = Number(b.unit_price || 0);
          return [i + 1, b.name || "", qty.toFixed(2), formatINR(up), formatINR(qty * up)];
        })}
      />
      <SectionTotal letter="C" label="Total Assembly BOP (C)" value={calc.assembly_bop_cost} />

      {/* Section D */}
      <SectionBar letter="D" title="ASSEMBLY TREATMENT & MAINTENANCE" />
      <table className="w-full border-collapse border border-slate-700">
        <tbody>
          <tr>
            <td className="border border-slate-700 p-1 px-2 w-[60mm] font-semibold">Surface Treatment</td>
            <td className="border border-slate-700 p-1 px-2 w-28 text-right font-mono">{formatINR(calc.assembly_surface_cost)}</td>
            <td className="border border-slate-700 p-1 px-2 italic text-slate-500 text-[11px]">{surfNotes}</td>
          </tr>
          <tr>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Heat Treatment</td>
            <td className="border border-slate-700 p-1 px-2 text-right font-mono">{formatINR(calc.assembly_heat_cost)}</td>
            <td className="border border-slate-700 p-1 px-2 italic text-slate-500 text-[11px]">{heatNotes}</td>
          </tr>
          <tr>
            <td className="border border-slate-700 p-1 px-2 font-semibold">Tool Insert &amp; Maintenance</td>
            <td className="border border-slate-700 p-1 px-2 text-right font-mono">{formatINR(calc.assembly_tool_maintenance)}</td>
            <td className="border border-slate-700 p-1 px-2 italic text-slate-500 text-[11px]">@ {tmPct.toFixed(1)}% of Assembly Process</td>
          </tr>
        </tbody>
      </table>
      <SectionTotal letter="D" label="Total Assembly Treatment & Maint (D)" value={calc.assembly_treatment_total} />

      {/* Sub-Total */}
      <div className="grid grid-cols-[40px_1fr_120px] bg-amber-200 border border-slate-700 my-2">
        <div></div>
        <div className="font-bold px-2 py-1.5">Sub-Total (A+B+C+D)</div>
        <div className="font-bold text-right px-2 py-1.5 font-mono">{formatINR(calc.sub_total)}</div>
      </div>

      {/* Section E */}
      <SectionBar letter="E" title="OVERHEADS & MARGIN" />
      <table className="w-full border-collapse border border-slate-700">
        <thead className="bg-amber-200 font-bold">
          <tr>
            <th className="border border-slate-700 p-1 w-10">#</th>
            <th className="border border-slate-700 p-1 text-left">Item</th>
            <th className="border border-slate-700 p-1 w-24 text-right">Value</th>
            <th className="border border-slate-700 p-1 w-44">Basis</th>
            <th className="border border-slate-700 p-1 w-28 text-right">₹/pc</th>
            <th className="border border-slate-700 p-1 w-40 text-left">Remarks</th>
          </tr>
        </thead>
        <tbody>
          {(calc.overheads_calculated || []).map((oh, i) => {
            const val = Number(oh.value || 0);
            return (
              <tr key={i}>
                <td className="border border-slate-700 p-1 text-center">{i + 1}</td>
                <td className="border border-slate-700 p-1 px-2">{oh.label || ""}</td>
                <td className="border border-slate-700 p-1 px-2 text-right font-mono">
                  {oh.basis === "lump_sum" ? `₹${val.toFixed(2)}` : `${(val * 100).toFixed(2)}%`}
                </td>
                <td className="border border-slate-700 p-1 px-2">{BASIS_LABELS[oh.basis] || ""}</td>
                <td className="border border-slate-700 p-1 px-2 text-right font-mono">{formatINR(oh.calculated || 0)}</td>
                <td className="border border-slate-700 p-1 px-2 italic text-slate-600">{oh.remarks || ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <SectionTotal letter="E" label="Total Overheads & Margin (E)" value={calc.overheads_and_margin} />

      {/* R46 — Assembly Section F: shown in customer quote only when admin toggled it on */}
      {(calc.assembly_custom_adjustments_calculated || []).length > 0 && (calc.assembly_custom_adjustments_total || 0) !== 0 && (
        <>
          <SectionBar letter="F" title="CUSTOM ADJUSTMENTS" />
          <div className="border-x border-b border-slate-700">
            {(calc.assembly_custom_adjustments_calculated || []).map((adj, i) => {
              const val = Number(adj.value || 0);
              const amt = Number(adj.amount || 0);
              const basisText = adj.mode === "pct"
                ? `${val >= 0 ? "+" : ""}${val.toFixed(2)}%`
                : `${val >= 0 ? "+" : ""}₹${val.toFixed(2)}/pc lumpsum`;
              return (
                <div key={`asm-adj-${i}`} className="grid grid-cols-[40px_1fr_140px] text-sm border-t border-slate-200 first:border-t-0">
                  <div className="px-2 py-1.5 text-center text-slate-500">{i + 1}</div>
                  <div className="px-2 py-1.5">
                    <span className="text-slate-800">{adj.label || "Adjustment"}</span>
                    <span className="text-xs text-slate-400 ml-2">({basisText})</span>
                  </div>
                  <div className="px-2 py-1.5 text-right font-mono">
                    {amt >= 0 ? "+ " : "− "}{formatINR(Math.abs(amt))}
                  </div>
                </div>
              );
            })}
          </div>
          <SectionTotal letter="F" label="Total Section F (Adjustments)" value={calc.assembly_custom_adjustments_total || 0} />
        </>
      )}

      {/* Grand Total */}
      <div className="grid grid-cols-[1fr_140px] bg-orange-500 text-white border border-slate-700 my-2">
        <div className="font-bold px-2 py-2 text-base">
          GRAND TOTAL / SELLING PRICE ({(calc.assembly_custom_adjustments_calculated || []).length > 0 && (calc.assembly_custom_adjustments_total || 0) !== 0 ? "A+B+C+D+E+F" : "A+B+C+D+E"})
        </div>
        <div className="font-bold text-right px-2 py-2 text-base font-mono">{formatINR(calc.grand_total)}</div>
      </div>

      {/* Development cost — one-time, separate from the per-piece selling price */}
      {(calc.development_cost_total || 0) > 0 && (
        <div className="grid grid-cols-[1fr_140px] bg-amber-50 border border-slate-700 my-2">
          <div className="font-bold px-2 py-1.5">
            DEVELOPMENT COST (one-time)
            <div className="font-normal italic text-slate-500 text-[11px]">Charged once — not included in the per-piece selling price above.</div>
          </div>
          <div className="font-bold text-right px-2 py-1.5 font-mono self-center">{formatINR(calc.development_cost_total)}</div>
        </div>
      )}

      {/* Development cost summary — from sub-parts + assembly-level → Total (one-time only) */}
      {(calc.development_cost_total || 0) > 0 && (
        <div className="border border-slate-700 my-2">
          <div className="bg-amber-100 text-amber-900 font-bold px-2 py-1.5 border-b border-slate-700">DEVELOPMENT COST SUMMARY (one-time)</div>
          <div className="grid grid-cols-[1fr_160px]">
            {(calc.components_development_cost || 0) > 0 && (<>
              <div className="px-2 py-1.5 border-b border-slate-200 text-slate-700">Development — from sub-parts</div>
              <div className="px-2 py-1.5 text-right font-mono border-b border-slate-200 text-slate-700">{formatINR(calc.components_development_cost)}</div>
            </>)}
            {(calc.assembly_development_cost || 0) > 0 && (<>
              <div className="px-2 py-1.5 border-b border-slate-200 text-slate-700">Development — assembly-level</div>
              <div className="px-2 py-1.5 text-right font-mono border-b border-slate-200 text-slate-700">{formatINR(calc.assembly_development_cost)}</div>
            </>)}
            <div className="px-2 py-2 font-bold bg-orange-100">Total Cost</div>
            <div className="px-2 py-2 text-right font-mono font-bold bg-orange-100">{formatINR(calc.development_cost_total)}</div>
          </div>
        </div>
      )}

      {/* Volumes & YoY schedule */}
      {(() => {
        const yoyPct = Number(asm.yoy_reduction_pct || 0);
        const lifeYears = Number(asm.project_life_years || 1);
        const yoy = calc.yoy_schedule || [];
        // Tightened gate: render only when YoY rate is set.
        const showYoy = yoyPct > 0 && yoy.length > 0;
        if (!showYoy) return null;
        return (
          <div className="mt-3">
            <div className="bg-orange-500 text-white font-bold px-2 py-1.5 border border-slate-700">
              VOLUMES &amp; YEAR-OVER-YEAR SCHEDULE
            </div>
            <div className="italic text-slate-500 text-[11px] py-1 px-1">
              YoY Reduction: {yoyPct.toFixed(1)}% per year compound · Project Life: {lifeYears} years
            </div>
            <table className="w-full text-[11px] border-collapse border border-slate-700">
              <thead className="bg-amber-200 text-slate-700 font-bold">
                <tr>
                  <th className="border border-slate-700 p-1 w-12 text-center">Year</th>
                  <th className="border border-slate-700 p-1 w-16 text-center">Calendar</th>
                  <th className="border border-slate-700 p-1 text-right">Price/pc (₹)</th>
                  <th className="border border-slate-700 p-1 text-right">Annual Vol (pcs)</th>
                  <th className="border border-slate-700 p-1 text-right">Monthly Vol</th>
                  <th className="border border-slate-700 p-1 text-right">Annual Value (₹)</th>
                </tr>
              </thead>
              <tbody>
                {yoy.map((s) => (
                  <tr key={s.year_number}>
                    <td className="border border-slate-700 p-1 text-center">Y{s.year_number}</td>
                    <td className="border border-slate-700 p-1 text-center">{s.year_label || ""}</td>
                    <td className="border border-slate-700 p-1 text-right font-mono">{formatINR(s.price_per_pc)}</td>
                    <td className="border border-slate-700 p-1 text-right font-mono">{Math.round(s.annual_volume_pcs).toLocaleString("en-IN")}</td>
                    <td className="border border-slate-700 p-1 text-right font-mono">{Math.round(s.monthly_volume_pcs || 0).toLocaleString("en-IN")}</td>
                    <td className="border border-slate-700 p-1 text-right font-mono">{formatINR(s.annual_value)}</td>
                  </tr>
                ))}
                <tr className="bg-orange-500 text-white font-bold">
                  <td colSpan={5} className="border border-slate-700 p-1.5 text-right">TOTAL PROJECT VALUE</td>
                  <td className="border border-slate-700 p-1.5 text-right">{formatINR(calc.total_project_value)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* T&C */}
      {tnc && (
        <div className="mt-4">
          <div className="bg-amber-200 text-slate-700 font-bold px-2 py-1 border border-slate-700">
            TERMS &amp; CONDITIONS
          </div>
          <div className="p-2 space-y-1 text-[11px]">
            {tnc.split("\n").map((line, i) => {
              const cleaned = (line || "").replace(/\*\*/g, "").trim();
              return cleaned ? <div key={i}>{cleaned}</div> : null;
            })}
            {gstLine && <div className="font-bold italic text-orange-600 mt-2">* {gstLine}</div>}
          </div>
        </div>
      )}

      {/* Signature */}
      <div className="grid grid-cols-2 mt-6">
        <div>
          {/* R30 — embed approver signature when approved + approver has signature */}
          {asm.approved_for_export && asm.approved_by && signatureUrl && (
            <div className="h-12 mb-1">
              <img
                src={signatureUrl}
                alt=""
                className="h-12 object-contain"
                data-testid="approver-signature"
              />
            </div>
          )}
          <div className="font-bold border-b border-slate-700 pb-1">For {companyName}</div>
          <div className="italic text-slate-500 text-[11px] pt-1">Authorised Signatory</div>
          {asm.approved_for_export && asm.approved_by_name && (
            <div className="text-[12px] font-semibold text-slate-900 pt-1" data-testid="approver-name">
              {asm.approved_by_name}
              {asm.approved_at && (
                <span className="italic text-slate-500 font-normal text-[11px] ml-2">
                  Approved: {String(asm.approved_at).slice(0, 10)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="pl-6">
          <div className="font-bold border-b border-slate-700 pb-6 mt-12">Customer Acceptance</div>
          <div className="italic text-slate-500 text-[11px] pt-1">Name &amp; Designation</div>
        </div>
      </div>
    </div>
  );
}

function SectionBar({ letter, title }) {
  return (
    <div className="bg-amber-100 border border-slate-700 grid grid-cols-[40px_1fr] mt-2">
      <div className="font-bold text-center py-1 border-r border-slate-700">{letter}</div>
      <div className="font-bold py-1 px-2">{title}</div>
    </div>
  );
}

function SectionTotal({ letter, label, value }) {
  return (
    <div className="grid grid-cols-[40px_1fr_120px] bg-amber-100 border border-t-0 border-slate-700">
      <div className="font-bold text-center py-1.5 border-r border-slate-700">{letter}</div>
      <div className="font-bold px-2 py-1.5">{label}</div>
      <div className="font-bold text-right px-2 py-1.5 font-mono">{formatINR(value)}</div>
    </div>
  );
}

function ItemTable({ headers, rows, widths }) {
  return (
    <table className="w-full border-collapse border border-slate-700">
      <thead className="bg-amber-200 font-bold">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className={`border border-slate-700 p-1 ${widths[i] || ""}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={headers.length} className="border border-slate-700 p-2 text-center italic text-slate-400">No items</td></tr>
        ) : rows.map((r, i) => (
          <tr key={i}>
            {r.map((cell, j) => (
              <td key={j} className={`border border-slate-700 p-1 px-2 ${widths[j]?.includes("right") ? "text-right font-mono" : ""}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
